'use client';

import * as React from 'react';
import { Button, type ButtonProps } from './button';
import { cn } from '@/utils';

export interface ToggleProps extends Omit<ButtonProps, 'children' | 'onChange' | 'isLoading' | 'asChild'> {
	checked: boolean;
	onCheckedChange?: (checked: boolean) => void;
}

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
	({ checked, onCheckedChange, className, disabled, onClick, variant, size = 'sm', shadow, ...props }, ref) => {
		const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
			if (disabled) {
				event.preventDefault();
				return;
			}
			onCheckedChange?.(!checked);
			onClick?.(event);
		};

		return (
			<Button
				type="button"
				role="switch"
				aria-checked={checked}
				aria-disabled={disabled}
				data-state={checked ? 'checked' : 'unchecked'}
				data-disabled={disabled ? 'true' : 'false'}
				ref={ref}
				onClick={handleClick}
				variant={variant}
				size={size}
				shadow={shadow}
				className={cn(
					'relative flex h-6 w-11 shrink-0 cursor-pointer items-center justify-start rounded-full p-0 transition-all',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60',
					'data-[state=unchecked]:bg-foreground/10',
					className,
				)}
				disabled={disabled}
				{...props}
			>
				<span
					className={cn('pointer-events-none h-4 w-4 translate-x-1 rounded-full bg-background transition-transform', checked && 'translate-x-5')}
				/>
			</Button>
		);
	},
);
Toggle.displayName = 'Toggle';
